/** Participant-facing assessment type — 5X copy only for classic 5x_v1. */

export const DEFAULT_IMPORTANCE_OPTIONS = [
  "Not Important",
  "Important",
  "Very Important",
  "Critical",
];

export const DEFAULT_AGREEMENT_OPTIONS = [
  "Not at all",
  "To a small extent",
  "To a moderate extent",
  "To a great extent",
  "Completely",
  "Unable to assess",
];

export const DEFAULT_PARTICIPANT_COPY = {
  welcome_description:
    "The 5x Team and Organization Assessment provides a CEO and Leadership Team with critical insights, including both strengths and improvement areas across the five critical drivers of PE management success—Strategic Clarity, Scalable Talent, Relentless Focus, Disciplined Execution, and an Energized Culture.",
  track_label_lite: "LITE assessment",
  track_label_full: "Full assessment",
  product_name_short: "5x Team Assessment",
  upgrade_offer_title: "LITE assessment complete",
  continue_full_title: "Full 5x Team and Organization Assessment",
  completed_full: "Your 5x Team Assessment has been completed successfully.",
  completed_lite: "Thank you for completing the LITE assessment.",
};

/** Neutral defaults for custom / tenant assessment types. */
export const GENERIC_PARTICIPANT_COPY = {
  welcome_description:
    "This assessment gathers your perspective on how the organization is performing across the areas that matter most. Your responses help surface strengths and priority improvement opportunities.",
  track_label_lite: "LITE assessment",
  track_label_full: "Full assessment",
  product_name_short: "Team Assessment",
  upgrade_offer_title: "LITE assessment complete",
  continue_full_title: "Full assessment",
  completed_full: "Your assessment has been completed successfully.",
  completed_lite: "Thank you for completing the LITE assessment.",
};

export const DEFAULT_PARTICIPANT_TYPE = {
  type_key: "5x_v1",
  name: "5X Team Assessment",
  sections: [
    { key: "strategic", label: "Strategic Clarity", order: 1 },
    { key: "scalable", label: "Scalable Talent", order: 2 },
    { key: "relentless", label: "Relentless Focus", order: 3 },
    { key: "disciplined", label: "Disciplined Execution", order: 4 },
    { key: "energized", label: "Energized Culture", order: 5 },
    { key: "financial", label: "Financial Performance", order: 6 },
  ],
  scales: [
    {
      id: "importance",
      label: "Level of Importance",
      options: [...DEFAULT_IMPORTANCE_OPTIONS],
    },
    {
      id: "agreement",
      label: "Level of Agreement",
      options: [...DEFAULT_AGREEMENT_OPTIONS],
    },
  ],
  tracks: ["lite", "full"],
  copy: { ...DEFAULT_PARTICIPANT_COPY },
};

export function isFiveXType(type) {
  if (type && type.uses_five_x_branding === false) return false;
  const key = String(type?.type_key || type?.key || "").trim();
  return key === "5x_v1";
}

function genericCopyForName(name) {
  const product =
    typeof name === "string" && name.trim() ? name.trim() : "Team Assessment";
  return {
    ...GENERIC_PARTICIPANT_COPY,
    product_name_short: product,
    continue_full_title: `Full ${product}`,
    completed_full: `Your ${product} has been completed successfully.`,
  };
}

function asOptionLabels(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => {
      if (typeof opt === "string") return opt;
      if (opt && typeof opt === "object" && typeof opt.label === "string") {
        return opt.label;
      }
      return "";
    })
    .filter(Boolean);
}

function mergeCopy(raw, { useFiveX, name } = {}) {
  const defaults = useFiveX
    ? DEFAULT_PARTICIPANT_COPY
    : genericCopyForName(name);
  const src = raw && typeof raw === "object" ? raw : {};
  const out = { ...defaults };
  for (const key of Object.keys(defaults)) {
    const val = src[key];
    if (typeof val === "string" && val.trim()) out[key] = val;
  }
  return out;
}

export function normalizeParticipantAssessmentType(raw, branding = null) {
  const src = raw && typeof raw === "object" ? raw : {};
  const useFiveX =
    src.uses_five_x_branding === false
      ? false
      : isFiveXType(src) || (!src.type_key && !src.name && !raw);
  const defaultScales = DEFAULT_PARTICIPANT_TYPE.scales;
  const rawScales = Array.isArray(src.scales) ? src.scales : [];
  const scales = defaultScales.map((fallback) => {
    const match = rawScales.find(
      (s) => s && String(s.id || "").toLowerCase() === fallback.id
    );
    const options = asOptionLabels(match?.options);
    return {
      id: fallback.id,
      label:
        typeof match?.label === "string" && match.label.trim()
          ? match.label
          : fallback.label,
      options: options.length ? options : [...fallback.options],
    };
  });

  const hasSections = Array.isArray(src.sections) && src.sections.length;
  const sections = hasSections
    ? src.sections
        .filter((s) => s && typeof s === "object")
        .map((s) => ({
          key: s.key,
          label: s.label,
          order: s.order,
        }))
    : useFiveX
      ? DEFAULT_PARTICIPANT_TYPE.sections.map((s) => ({ ...s }))
      : [];

  const tracks = Array.isArray(src.tracks) && src.tracks.length
    ? src.tracks.filter((t) => typeof t === "string" && t)
    : [...DEFAULT_PARTICIPANT_TYPE.tracks];

  const brandingName =
    branding?.product_display_name?.trim() ||
    branding?.tenant_name?.trim() ||
    "";
  const name =
    typeof src.name === "string" && src.name.trim()
      ? src.name
      : useFiveX
        ? DEFAULT_PARTICIPANT_TYPE.name
        : brandingName || "Custom Assessment";

  const normalized = {
    type_key: src.type_key || (useFiveX ? DEFAULT_PARTICIPANT_TYPE.type_key : "custom"),
    name,
    sections,
    scales,
    tracks,
    copy: mergeCopy(src.copy || src.participant_copy, { useFiveX, name }),
    uses_five_x_branding: src.uses_five_x_branding ?? useFiveX,
  };
  return normalized;
}

export function scaleOptions(type, scaleId) {
  const t = normalizeParticipantAssessmentType(type);
  const scale = t.scales.find(
    (s) => String(s.id || "").toLowerCase() === String(scaleId || "").toLowerCase()
  );
  return scale?.options?.length ? [...scale.options] : [];
}

export function scaleLabel(type, scaleId) {
  const t = normalizeParticipantAssessmentType(type);
  const scale = t.scales.find(
    (s) => String(s.id || "").toLowerCase() === String(scaleId || "").toLowerCase()
  );
  if (scale?.label) return scale.label;
  return scaleId === "agreement" ? "Level of Agreement" : "Level of Importance";
}

export function shortScaleName(type, scaleId) {
  return scaleLabel(type, scaleId).replace(/^Level of\s+/i, "");
}

export function trackLabel(type, track) {
  const t = normalizeParticipantAssessmentType(type);
  if (track === "lite") return t.copy.track_label_lite;
  if (track === "full") return t.copy.track_label_full;
  return "";
}

export function welcomeDescription(type) {
  return normalizeParticipantAssessmentType(type).copy.welcome_description;
}

export function productName(type, branding = null) {
  const t = normalizeParticipantAssessmentType(type, branding);
  if (!t.uses_five_x_branding) {
    return t.copy.product_name_short || "Team Assessment";
  }
  return t.copy.product_name_short || t.name;
}

export function upgradeOfferTitle(type) {
  return normalizeParticipantAssessmentType(type).copy.upgrade_offer_title;
}

export function continueFullTitle(type) {
  return normalizeParticipantAssessmentType(type).copy.continue_full_title;
}

export function completedFull(type) {
  return normalizeParticipantAssessmentType(type).copy.completed_full;
}

export function completedLite(type) {
  return normalizeParticipantAssessmentType(type).copy.completed_lite;
}

export function formatOptionList(options) {
  const list = (options || []).filter(Boolean);
  if (!list.length) return "";
  if (list.length === 1) return `${list[0]}.`;
  if (list.length === 2) return `${list[0]} or ${list[1]}.`;
  return `${list.slice(0, -1).join(", ")}, or ${list[list.length - 1]}.`;
}
